import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { fetchQuery } from "react-relay";

import {
	faCog,
	faInfoCircle,
	faList,
	faProjectDiagram,
	faSearch,
	faTimes,
} from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { compact, debounce, noop } from "lodash";

import AssigneeFilter, { type AssigneeType, initialAssigneesQuery } from "~/common/AssigneeFilter";
import ViewportContext from "~/common/ViewportContext";
import { decodeGlobalId } from "~/lib/relayUtil";
import { nGetText } from "~/lib/utils";
import { environment } from "~/models";
import CycleListInput, { cycleListQuery, type CycleType } from "~/okrs/components/CycleListInput";
import { StatusNames } from "~/okrs/components/StatusInput";
import CreateObjective from "~/okrs/CreateObjective";
import { OKRsContext } from "~/okrs/ObjectiveList";
import StreamSettingsModal from "~/stream/StreamSettings";
import TeamSettingsModal from "~/team/TeamSettings";
import { Button, useModals } from "~/ui";
import ArrowDropdown, { DropdownItem } from "~/ui/ArrowDropdown";
import Popup from "~/ui/legacy/Popup";
import { IconButton } from "~/ui/legacyFellowIndex";
import LoadingSpinner from "~/ui/loading/LoadingSpinner";
import toast from "~/ui/toast";

import type { AssigneeFilterInitialUsersQuery } from "~/__generated__/AssigneeFilterInitialUsersQuery.graphql";
import type { CycleListInputQuery } from "~/__generated__/CycleListInputQuery.graphql";
import type { ObjectiveStatus } from "~/__generated__/ObjectiveQueryQuery.graphql";

import styles from "./Filters.module.css";

export enum Source {
	STREAM = "STREAM",
	TEAM = "TEAM",
}

type FiltersProps = {
	fromSource?: string;
	source?: Source;
};

export default function Filters({ fromSource, source }: FiltersProps) {
	const { filters } = useContext(OKRsContext);
	const { isMobile } = useContext(ViewportContext);
	// For the search input we use a local state for the input value and propagate with a debounce to the parent
	const [search, setSearch] = useState<string>(filters?.filterName || "");
	const modals = useModals();

	const updateFilterName = useCallback(
		(newFilterName: string) => {
			filters?.onChangeFilterName(newFilterName);
		},
		[filters],
	);

	const debouncedUpdateFilterName = useMemo(() => debounce(updateFilterName, 500), [updateFilterName]);

	const switchToTree = useCallback(() => filters?.onChangeView("TREE"), [filters]);
	const switchToList = useCallback(() => filters?.onChangeView("LIST"), [filters]);

	const onChangeSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearch(event.target.value);
		debouncedUpdateFilterName(event.target.value);
	};

	const onClearSearch = () => {
		setSearch("");
		filters?.onChangeFilterName("");
	};

	const onMobileFilterClick = useCallback(() => {
		toast.success("Access Fellow on a desktop to apply filters to your objectives.");
	}, []);

	const onClickCreateObjective = useCallback(() => {
		modals.open(({ close }) => <CreateObjective onClose={close} />);
	}, [modals]);

	return (
		<div className={styles.filters}>
			{!isMobile && (
				<div className={styles.viewButtons}>
					<Popup
						inverted
						position="top center"
						size="tiny"
						content="List view"
						trigger={
							<Button
								size="wide"
								className={cx({ [styles.activeViewButton]: filters?.view === "LIST" })}
								onClick={switchToList}
							>
								<FontAwesomeIcon fixedWidth icon={faList} />
							</Button>
						}
					/>
					<Popup
						inverted
						position="top center"
						size="tiny"
						content="Tree view"
						trigger={
							<Button
								size="wide"
								className={cx({ [styles.activeViewButton]: filters?.view === "TREE" })}
								onClick={switchToTree}
							>
								<FontAwesomeIcon fixedWidth icon={faProjectDiagram} />
							</Button>
						}
					/>
				</div>
			)}

			<label className={cx(styles.filterName, { [styles.noGrow]: fromSource })}>
				<FontAwesomeIcon fixedWidth icon={faSearch} />
				<input type="search" placeholder="Search objectives" value={search} onChange={onChangeSearch} />
				{search && (
					<button title="Clear search" className={styles.searchClearButton} onClick={onClearSearch}>
						<FontAwesomeIcon fixedWidth icon={faTimes} />
					</button>
				)}
			</label>

			{fromSource && source ? (
				<>
					<FiltersReadonly
						sourceId={fromSource}
						source={source}
						selectedAssigneeIds={filters?.initialFilterAssignees}
						status={filters?.filterStatus}
						cycles={filters?.filterCycles}
					/>
					<Button variant="primary" size="normal" className={styles.createButton} onClick={onClickCreateObjective}>
						Create objective
					</Button>
				</>
			) : !isMobile ? (
				<>
					<AssigneeFilter
						initialAssigneeIds={filters?.initialFilterAssignees}
						values={filters?.filterAssignee || []}
						setValues={filters?.onChangeFilterAssignee || noop}
					/>

					<ArrowDropdown
						heading="Select status"
						text={filters?.filterStatus ? StatusNames[filters?.filterStatus] : "All statuses"}
						className={styles.filterStatus}
						textClassName={styles.dropdownText}
					>
						<DropdownItem key="status-none" onClick={filters?.onChangeFilterStatus(undefined)}>
							All statuses
						</DropdownItem>
						{Object.keys(StatusNames).map(status => (
							<DropdownItem key={`status-${status}`} onClick={filters?.onChangeFilterStatus(status as ObjectiveStatus)}>
								{StatusNames[status as ObjectiveStatus]}
							</DropdownItem>
						))}
					</ArrowDropdown>

					<React.Suspense fallback={<LoadingSpinner inline />}>
						<CycleListInput
							isMulti
							selected={filters?.filterCycles}
							onChange={filters?.onChangeFilterCycles || noop}
							className={styles.dropdown}
						/>
					</React.Suspense>
				</>
			) : (
				<Button size="wide" className={styles.mobileFilter} onClick={onMobileFilterClick}>
					<FontAwesomeIcon fixedWidth icon={faList} />
				</Button>
			)}
		</div>
	);
}

type FiltersReadonlyProps = {
	sourceId: string;
	source: Source;
	selectedAssigneeIds?: string[];
	status?: ObjectiveStatus;
	cycles?: string[];
};

function FiltersReadonly({ sourceId, source, selectedAssigneeIds, status, cycles }: FiltersReadonlyProps) {
	const modalCtx = useModals();
	const [loadedAssignees, setLoadedAssignees] = useState<AssigneeType[]>([]);
	const [loadedCycles, setLoadedCycles] = useState<CycleType[]>([]);
	const { team } = useContext(OKRsContext);
	const disableEdit = team && team.isAccountWideTeam;

	useEffect(() => {
		if (!!selectedAssigneeIds && selectedAssigneeIds.length > 0) {
			const userIds = selectedAssigneeIds.filter(i => decodeGlobalId(i)[0] === "User");
			const teamIds = selectedAssigneeIds.filter(i => decodeGlobalId(i)[0] === "Team");

			fetchQuery<AssigneeFilterInitialUsersQuery>(environment, initialAssigneesQuery, {
				userIds: userIds,
				teamIds: teamIds,
			})
				.toPromise()
				.then(response => {
					if (!response || !response.teamsById || !response.usersById) {
						return;
					}

					setLoadedAssignees([
						...response.teamsById.map(team => ({ team })),
						...response.usersById.map(user => ({ user })),
					]);
				});
		} else {
			setLoadedAssignees([]);
		}
	}, [selectedAssigneeIds, setLoadedAssignees]);

	useEffect(() => {
		fetchQuery<CycleListInputQuery>(environment, cycleListQuery, {})
			.toPromise()
			.then(response => {
				if (!response?.viewer.cycles) {
					return;
				}

				const cyclesToSet = compact(
					response.viewer.cycles.edges.map(edge => edge?.node.id && cycles?.includes(edge?.node.id) && edge?.node),
				);

				setLoadedCycles(cyclesToSet);
			});
	}, [cycles]);

	const openStreamSettings = useCallback(() => {
		if (!sourceId) return;

		modalCtx?.open(({ close }) =>
			source === Source.STREAM ? (
				<StreamSettingsModal close={close} streamId={sourceId} defaultSection="objectives" />
			) : (
				<TeamSettingsModal close={close} teamId={sourceId} />
			),
		);
	}, [sourceId, source, modalCtx]);

	const assignedToPart = (
		<div>
			Assigned to:{" "}
			<b>
				{loadedAssignees.length > 0
					? loadedAssignees.map(a => a.user?.fullName || a.team?.title).join(", ")
					: "All teams & users"}
			</b>
		</div>
	);
	const statusPart = (
		<div>
			Status: <b>{status ? StatusNames[status] : "All statuses"}</b>
		</div>
	);
	const cyclePart = (
		<div>
			Cycle:{" "}
			<b>
				{loadedCycles?.length
					? `${loadedCycles?.length} ${nGetText(loadedCycles?.length, "cycle", "cycles")} selected`
					: "Current cycles"}
			</b>
		</div>
	);

	return (
		<div className={styles.fromStreamWrapper}>
			<Popup
				inverted
				size="tiny"
				on="hover"
				position="bottom center"
				trigger={
					<div>
						Filters applied <FontAwesomeIcon icon={faInfoCircle} />
					</div>
				}
			>
				<>
					{assignedToPart}
					{statusPart}
					{cyclePart}
				</>
			</Popup>
			{!disableEdit && (
				<IconButton title="Open filters" icon={faCog} onClick={openStreamSettings} className={styles.cogButton} />
			)}
		</div>
	);
}
